// The week: the current lesson as a hub (docs/COURSE.md "Lesson hub"). Its meta names everything
// the week asks of you; this module only works out which lesson is the week's and which of its
// six steps are done, from data the app already has. Pure (tested in test/week.test.ts).
//
//   Tunti      the lesson's check passed
//   Harjoitus  today's daily cards done (Tänään knows)
//   Kuuntele   the lesson's show lines — nothing records listening, so no done state
//   Puhu       a recording of yours since the week began, of a focus word or line
//   Kirje      a letter of yours in Kirjeet, or the notebook letter marked done, since the week began
//   Yhdessä    Illan tie's done record of the week's lesson for you (the evening's Valmis or
//              "Olemme valmiita": one write ticks both, whichever phone; engine pedagogy/evening.ts).
//              Until 2026-10-13 an own scene take ticked it, which missed every evening done on one
//              phone or offline; that rule is gone.
import type { Letter, LessonProgressRow, NotebookDone } from '@valyrio/engine/api';
import type { EveningDone } from '@valyrio/engine/pedagogy/evening';
import type { PaceConfig } from '@valyrio/engine/types';
import type { LessonFileMeta } from '../lessons';
import { localDate, unlockDateOf } from '../lib/engine';

export type WeekStep = 'lesson' | 'practice' | 'listen' | 'speak' | 'letter' | 'together';
export const WEEK_STEPS: readonly WeekStep[] = ['lesson', 'practice', 'listen', 'speak', 'letter', 'together'];

/** done / not done; 'none' = nothing records it (shown without a tick). */
export type StepState = 'done' | 'todo' | 'none';

export interface WeekLesson {
  meta: LessonFileMeta;
  /** The local day the week began: the lesson's Saturday, or my pass of the lesson before when later. */
  since: string;
}

/** The week's lesson: the newest lesson open for both (null before the course starts). */
export function weekLesson(metas: readonly LessonFileMeta[], open: number, pace: PaceConfig, progress: readonly LessonProgressRow[]): WeekLesson | null {
  const sorted = [...metas].sort((a, b) => a.order - b.order);
  const meta = [...sorted].reverse().find((m) => m.order <= open);
  if (!meta) return null;
  let since = unlockDateOf(meta.order, pace);
  const prev = sorted.filter((m) => m.order < meta.order).pop();
  const prevPass = prev ? progress.find((p) => p.lessonId === prev.id)?.passedAt : null;
  if (prevPass) {
    const d = localDate(new Date(prevPass), pace.timezone);
    if (d > since) since = d;
  }
  return { meta, since };
}

/** Recording targets of the week's Puhu step: its lines and its words (lemma ids). */
export function speakTargets(meta: LessonFileMeta, lemmaIdOf: (word: string) => string | null): Set<string> {
  const p = meta.pronunciation;
  const out = new Set<string>();
  for (const id of p?.lines ?? []) out.add(`dialogue:${id}`);
  for (const w of p?.words ?? []) {
    const id = lemmaIdOf(w);
    if (id) out.add(`lemma:${id}`);
  }
  return out;
}

export interface WeekInputs {
  meId: string;
  week: WeekLesson;
  pace: PaceConfig;
  progress: readonly LessonProgressRow[];
  /** Today's daily cards are done (Tänään: the session ended, or nothing was left today). */
  practiceDone: boolean;
  /** My recordings (null = not loaded). */
  recordings: readonly { userId: string; target: string; createdAt: string }[] | null;
  letters: readonly Pick<Letter, 'author' | 'createdAt'>[] | null;
  notebookDone: readonly Pick<NotebookDone, 'day' | 'taskKey'>[];
  speak: ReadonlySet<string>;
  /** Illan tie's done rows (both people's; null = not loaded). */
  evening: readonly Pick<EveningDone, 'lessonId' | 'userId'>[] | null;
}

export function weekSteps(i: WeekInputs): Record<WeekStep, StepState> {
  const day = (iso: string) => localDate(new Date(iso), i.pace.timezone);
  const since = i.week.since;
  const mine = (i.recordings ?? []).filter((r) => r.userId === i.meId && day(r.createdAt) >= since);
  const passed = i.progress.some((p) => p.lessonId === i.week.meta.id && !!p.passedAt);
  const letter =
    (i.letters ?? []).some((l) => l.author === i.meId && day(l.createdAt) >= since) || i.notebookDone.some((n) => n.taskKey === 'letter' && n.day >= since);
  return {
    lesson: passed ? 'done' : 'todo',
    practice: i.practiceDone ? 'done' : 'todo',
    listen: 'none',
    speak: i.speak.size ? (mine.some((r) => i.speak.has(r.target)) ? 'done' : 'todo') : 'none',
    letter: letter ? 'done' : 'todo',
    together: (i.evening ?? []).some((d) => d.lessonId === i.week.meta.id && d.userId === i.meId) ? 'done' : 'todo',
  };
}
